import { useCallback, useEffect, useState } from "react";
import { getEmailMeta, type EmailMeta } from "@/api/email";
import { useAuthStore } from "@/stores/auth.store";

// One read per user, project and session: the meta (the project, what the
// user may do in it, its variables and statuses) changes only with a role or
// the project's settings. `reload()` refreshes it.
const cached = new Map<string, EmailMeta>();
const inflight = new Map<string, Promise<EmailMeta>>();

/** The meta of one project, read once per user and session (shared cache). */
export function fetchMeta(userId: string, slug: string, force = false): Promise<EmailMeta> {
  const key = `${userId}:${slug}`;
  const hit = cached.get(key);
  if (!force && hit) return Promise.resolve(hit);
  const pending = inflight.get(key);
  if (!force && pending) return pending;
  const promise = getEmailMeta(slug).then((meta) => {
    cached.set(key, meta);
    return meta;
  });
  inflight.set(key, promise);
  void promise.finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });
  return promise;
}

/** The Email module of the project in the URL: its access, variables and
 *  statuses. Another project's never shows here. */
export function useEmailMeta(slug: string | null | undefined) {
  const userId = useAuthStore((s) => s.user?.id ?? "");
  const key = slug ? `${userId}:${slug}` : "";
  const [state, setState] = useState<{ key: string; meta: EmailMeta | null }>(() => ({
    key,
    meta: key ? (cached.get(key) ?? null) : null,
  }));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !slug) return;
    let cancelled = false;
    setError(null);
    fetchMeta(userId, slug)
      .then((meta) => !cancelled && setState({ key: `${userId}:${slug}`, meta }))
      .catch(() => !cancelled && setError("The Email module could not be loaded."));
    return () => {
      cancelled = true;
    };
  }, [userId, slug]);

  const reload = useCallback(async () => {
    if (!userId || !slug) return;
    setState({ key: `${userId}:${slug}`, meta: await fetchMeta(userId, slug, true) });
  }, [userId, slug]);

  // Never another project's meta, even for a render after the slug changed.
  const meta = state.key === key ? state.meta : null;
  return { meta, access: meta?.project ?? null, error, reload };
}
